'use client';

import React, { useState, useEffect } from 'react';


interface BlastSidebarProps {
  activeSection: string;
  onNavigate: (section: string) => void;
  isOpen: boolean;
}

const tocItems = [
  { id: 'hero', label: 'Our Services', stat: '§ 01' },
  { id: 'stats', label: 'Scale & Proof', stat: '§ 02' },
  { id: 'surfaces', label: 'Surface Types', stat: '§ 03' },
  { id: 'comparison', label: 'ROI Comparison', stat: '§ 04' },
];

const ctaLabels: Record<string, string> = {
  hero: 'Get a Free Quote',
  stats: 'Book a Service',
  surfaces: 'Get a Surface Quote',
  comparison: 'Request an Estimate',
};

const ctaSubLabels: Record<string, string> = {
  hero: 'Fast response — same day service',
  stats: 'Residential & commercial available',
  surfaces: 'Per sq ft breakdowns inside',
  comparison: 'Washing vs. repainting vs. decline',
};

export default function BlastSidebar({ activeSection, onNavigate, isOpen }: BlastSidebarProps) {
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCtaClick = () => {
    onNavigate('capture');
  };

  return (
    <aside className={`blast-sidebar ${isOpen ? 'open' : ''}`}>
      {/* Scroll Progress Bar */}
      <div
        className="absolute top-0 left-0 w-full h-0.5 z-10"
        style={{ background: 'rgba(255,255,255,0.08)' }}
      >
        <div
          className="h-full transition-all duration-150"
          style={{ width: `${scrollProgress}%`, background: 'var(--accent-blue)' }}
        />
      </div>

      {/* Logo */}
      <div className="px-6 pt-8 pb-6 border-b" style={{ borderColor: 'rgba(209,217,224,0.12)' }}>
        <div className="flex items-center gap-3 mb-1">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'var(--accent-blue)' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </div>
          <div>
            <span
              className="font-dm font-700 text-base tracking-tight"
              style={{ color: 'var(--arctic-white)', fontWeight: 700 }}
            >
              Lake Country Cleaning
            </span>
            <div className="tooltip-text" style={{ marginTop: '1px' }}>Pressure Washing Co.</div>
          </div>
        </div>
      </div>

      {/* Table of Contents */}
      <nav className="flex-1 px-6 py-6 overflow-y-auto">
        <div className="tooltip-text mb-4">Navigation</div>
        <ul className="space-y-1">
          {tocItems.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => onNavigate(item.id)}
                className={`toc-item w-full text-left py-2.5 flex items-center justify-between group transition-all duration-300 ${
                  activeSection === item.id ? 'active' : ''
                }`}
              >
                <span
                  className="text-sm font-medium leading-tight"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  {item.label}
                </span>
                <span
                  className="text-xs font-mono flex-shrink-0 ml-2 transition-colors duration-300"
                  style={{
                    color: activeSection === item.id
                      ? 'var(--accent-blue)'
                      : 'rgba(209,217,224,0.3)',
                    fontSize: '0.625rem',
                    letterSpacing: '0.05em',
                  }}
                >
                  {item.stat}
                </span>
              </button>
            </li>
          ))}
        </ul>

        {/* Divider */}
        <div
          className="my-6"
          style={{ height: '1px', background: 'rgba(209,217,224,0.12)' }}
        />

        {/* Contextual CTA */}
        <div className="space-y-3">
          <button className="sidebar-cta" onClick={handleCtaClick}>
            {ctaLabels[activeSection] || 'Get a Free Quote'}
          </button>
          <p
            className="text-center"
            style={{
              fontSize: '0.6875rem',
              color: 'rgba(209,217,224,0.4)',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              letterSpacing: '0.02em',
            }}
          >
            {ctaSubLabels[activeSection] || 'Fast response — same day service'}
          </p>
        </div>
      </nav>

      {/* Bottom Badge */}
      <div
        className="px-6 py-4 border-t"
        style={{ borderColor: 'rgba(209,217,224,0.12)' }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ background: '#22c55e', boxShadow: '0 0 6px rgba(34,197,94,0.6)' }}
          />
          <span
            style={{
              fontSize: '0.6875rem',
              color: 'rgba(209,217,224,0.5)',
              fontFamily: 'Plus Jakarta Sans, sans-serif',
              letterSpacing: '0.02em',
            }}
          >
            Serving the Greater Metro Area
          </span>
        </div>
        <p
          className="mt-2"
          style={{
            fontSize: '0.625rem',
            color: 'rgba(209,217,224,0.25)',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            letterSpacing: '0.05em',
          }}
        >
          © 2026 Lake Country Cleaning
        </p>
      </div>
    </aside>
  );
}